'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useTransition } from 'react';
import { X } from 'lucide-react';
import { type PublicRouteRatesFilterOptions } from '@ra-mr/types';
import { SearchableSelectField } from '@/components/ui/searchable-select-field';

export interface HubRouteRateFilters {
  origin?: string;
  destination?: string;
  equipment?: string;
}

interface HubRouteRatesFiltersProps {
  options: PublicRouteRatesFilterOptions;
  values: HubRouteRateFilters;
}

type FilterKey = keyof HubRouteRateFilters;

export const HubRouteRatesFilters = ({
  options,
  values,
}: HubRouteRatesFiltersProps) => {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const hasFilters = Object.values(values).some(Boolean);

  const navigate = (params: URLSearchParams) => {
    const query = params.toString();

    startTransition(() => {
      router.replace(query ? `${pathname}?${query}` : pathname, {
        scroll: false,
      });
    });
  };

  const updateFilter = (key: FilterKey, value: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete('provider');
    params.delete('page');

    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }

    navigate(params);
  };

  const clearFilters = () => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete('origin');
    params.delete('destination');
    params.delete('equipment');
    params.delete('provider');
    params.delete('page');
    navigate(params);
  };

  const triggerClassName =
    'h-10 rounded-lg border-slate-200 bg-white text-sm font-medium text-slate-700 shadow-none hover:border-slate-300 focus:ring-slate-200';

  return (
    <div
      className={`mb-6 grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.8fr)_auto] ${
        isPending ? 'opacity-70' : ''
      }`}
    >
      <SearchableSelectField
        id="route-rate-origin"
        label="From"
        placeholder="All origins"
        searchPlaceholder="Search origins..."
        value={values.origin ?? ''}
        options={options.origins}
        disabled={isPending}
        triggerClassName={triggerClassName}
        onValueChange={(value) => {
          updateFilter('origin', value);
        }}
      />

      <SearchableSelectField
        id="route-rate-destination"
        label="To"
        placeholder="All destinations"
        searchPlaceholder="Search destinations..."
        value={values.destination ?? ''}
        options={options.destinations}
        disabled={isPending}
        triggerClassName={triggerClassName}
        onValueChange={(value) => {
          updateFilter('destination', value);
        }}
      />

      <SearchableSelectField
        id="route-rate-equipment"
        label="Equipment"
        placeholder="All equipment"
        searchPlaceholder="Search equipment..."
        value={values.equipment ?? ''}
        options={options.equipment}
        disabled={isPending}
        triggerClassName={triggerClassName}
        onValueChange={(value) => {
          updateFilter('equipment', value);
        }}
      />

      <div className="flex items-end sm:col-span-2 lg:col-span-1">
        {hasFilters && (
          <button
            type="button"
            onClick={clearFilters}
            disabled={isPending}
            className="inline-flex h-10 items-center gap-1.5 px-2 text-sm font-semibold text-slate-500 transition-colors hover:text-slate-900 disabled:cursor-wait disabled:opacity-60"
          >
            <X className="h-4 w-4" />
            Clear
          </button>
        )}
      </div>
    </div>
  );
};
